import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import toast from 'react-hot-toast';
import { useAuth } from '../App.jsx';
import { 
  Search, UserCheck, Inbox, Clock, CheckCircle, XCircle, ChevronRight, 
  Filter, BookOpen, DollarSign, User, Calendar, ShieldCheck, Edit3 
} from 'lucide-react';
import api from '../services/api.js';
import GestionarRepertoriosModal from '../components/GestionarRepertoriosModal';
import CotizacionTramiteModal from '../components/CotizacionTramiteModal.jsx';
import FinalizarTramiteModal from '../components/FinalizarTramiteModal.jsx';
import RectificarTramiteModal from '../components/Tramites/RectificarTramiteModal.jsx';
import { formatRepertoriosRange } from '../utils/formatters.js';
export default function ConsolaFuncionario() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  
  const { user, hasRole } = useAuth();
  const [tramites, setTramites] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState(searchParams.get('search') || '');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [mostrarSoloNuevos, setMostrarSoloNuevos] = useState(false);
  const [tab, setTab] = useState(hasRole('boss') ? 'inbox' : 'mis-tramites');
  const [assigning, setAssigning] = useState({});
  const [repertorioModal, setRepertorioModal] = useState({ isOpen: false, tramite: null });
  const [cotizacionModal, setCotizacionModal] = useState({ isOpen: false, tramite: null });
  const [finalizarModal, setFinalizarModal] = useState({ isOpen: false, tramite: null });
  const [rectificarModal, setRectificarModal] = useState({ isOpen: false, tramite: null });

  const isBoss  = hasRole('boss') || hasRole('super_admin');
  const isStaff = hasRole('staff');
  const canAssign = isBoss || hasRole('admin') || Boolean(user?.can_assign_tramites);

  useEffect(() => {
    const s = searchParams.get('search');
    if (s !== null) {
      setBusqueda(s);
    }
  }, [searchParams]);

  const cargarTramites = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: busqueda || undefined,
        estado_id: estadoFiltro || undefined,
        unread_only: mostrarSoloNuevos ? 1 : 0,
      };

      if (tab === 'inbox') {
          params.unassigned = true;
          // Limpiar otros si existen
          delete params.assigned;
          delete params.mine;
      } else if (tab === 'mis-tramites') {
          if (isBoss) {
              params.assigned = true;
              delete params.mine;
          } else {
              params.mine = true;
              delete params.assigned;
          }
          delete params.unassigned;
      }

      const res = await api.get('/tramites', { params });
      setTramites(res.data.data);
    } catch {
      toast.error('Error al cargar trámites.');
    } finally {
      setLoading(false);
    }
  }, [busqueda, estadoFiltro, tab, mostrarSoloNuevos]);

  const cargarStaff = useCallback(async () => {
    try {
      const res = await api.get('/users/assignable');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setStaffList(list);
    } catch {
      console.error('Error al cargar lista de staff.');
    }
  }, []);

  useEffect(() => { cargarTramites(); }, [cargarTramites]);
  useEffect(() => { cargarStaff();    }, [cargarStaff]);

  const handleAssign = async (tramiteId, staffId) => {
    setAssigning(p => ({ ...p, [tramiteId]: true }));
    const newId = staffId ? Number(staffId) : null;
    setTramites(prev => prev.map(t => t.id === tramiteId ? { ...t, asignado_a: newId } : t));
    try {
      const res = await api.patch(`/tramites/${tramiteId}/asignar`, { 
        asignado_a: newId,
      });
      toast.success(res.data?.message || (staffId ? 'Trámite asignado.' : 'Asignación removida.'));
      cargarTramites();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Error al asignar trámite.');
      cargarTramites();
    } finally {
      setAssigning(p => ({ ...p, [tramiteId]: false }));
    }
  };

  const ESTADO_CONFIG = {
    'Ingresado': { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe', dot: '#3b82f6' },
    'Procesando': { bg: '#fffbeb', text: '#92400e', border: '#fde68a', dot: '#f59e0b' },
    'Completado': { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', dot: '#10b981' },
    'Pagado': { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', dot: '#22c55e' },
    'Rehusado': { bg: '#fef2f2', text: '#991b1b', border: '#fecaca', dot: '#ef4444' },
    'Reabierto': { bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff', dot: '#a855f7' },
    'Comprobante Pago Recibido': { bg: '#f0fdfa', text: '#115e59', border: '#99f6e4', dot: '#14b8a6' },
  };

  return (
    <div className="container" style={{ maxWidth: '100%', padding: '2rem clamp(16px, 4vw, 48px)', minHeight: '80vh' }}>
      <div className="animate-fade-in" style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '2rem' }}>💼</span>
          <h1 className="section-title" style={{ marginBottom: 0 }}>Asignación de trámites</h1>
        </div>
        <span className="accent-line" />
        <p className="text-muted">Gestión de trámites y asignación de flujo de trabajo.</p>
      </div>

      {/* Tabs / Dashboard Picker */}
      <div className="tabs" style={{ marginBottom: '2rem' }}>
        {isBoss && (
          <button className={`tab ${tab === 'inbox' ? 'active' : ''}`} onClick={() => setTab('inbox')}>
            <Inbox size={16} /> Bandeja de Entrada (Sin Asignar)
          </button>
        )}
        <button className={`tab ${tab === 'mis-tramites' ? 'active' : ''}`} onClick={() => setTab('mis-tramites')}>
          <Clock size={16} /> {isBoss ? 'Asignados Global' : 'Mis Trámites'}
        </button>
      </div>

      {/* Filtros Rápidos */}
      <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ margin: 0, flex: 1 }}>
          <label>Buscar Trámite</label>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
            <input
              placeholder="RUT, Nombre o Seguimiento..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
            />
          </div>
        </div>
        
        <div className="form-group" style={{ margin: 0, width: 200 }}>
          <label>Estado</label>
          <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
            <option value="">Cualquier Estado</option>
            <option value="2">Ingresado</option>
            <option value="3">Procesando</option>
            <option value="4">Completado</option>
            <option value="5">Rehusado</option>
            <option value="11">Reabierto</option>
          </select>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '0.5rem' }}>
          <div style={{ 
            width: 40, height: 22, borderRadius: 12, background: mostrarSoloNuevos ? '#ef4444' : '#e2e8f0', 
            position: 'relative', transition: 'background 0.3s'
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 2, left: mostrarSoloNuevos ? 20 : 2, transition: 'left 0.3s'
            }}></div>
          </div>
          Solo con nuevas respuestas
        </label>
      </div>

      {/* Listado */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}><span className="spinner spinner-lg" /></div>
      ) : tramites.length === 0 ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3>¡Todo al día!</h3>
          <p className="text-muted">No hay trámites pendientes en esta vista.</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }} className="animate-fade-in">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>Seguimiento</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>Repertorios</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b' }}>Solicitante / Tipo</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>Área</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>Estado</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', minWidth: '180px' }}>Gestión</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>Caja</th>
                  <th style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', textAlign: 'right', whiteSpace: 'nowrap' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tramites.map(t => (
                  <tr 
                    key={t.id} 
                    style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.15s ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Seguimiento */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                        {t.numero_seguimiento}
                      </div>
                      {t.has_unread_reply && (
                        <div style={{ marginTop: 3 }}>
                          <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: 6, height: 6, background: '#ef4444', borderRadius: '50%', display: 'inline-block' }} />
                            Nueva Respuesta
                          </span>
                        </div>
                      )}
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={11} /> {new Date(t.created_at).toLocaleDateString('es-CL')}
                      </div>
                    </td>

                    {/* Repertorios */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {t.exento_repertorio ? (
                        <span 
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            backgroundColor: '#ecfdf5',
                            color: '#065f46',
                            fontWeight: 700,
                            fontSize: '0.74rem',
                            border: '1px solid #a7f3d0'
                          }}
                          title={`Trámite exento de repertorio previo${t.motivo_exento_repertorio ? `: ${t.motivo_exento_repertorio}` : ''}`}
                        >
                          <ShieldCheck size={12} color="#059669" /> Exento
                        </span>
                      ) : t.repertorios && t.repertorios.length > 0 ? (
                        <span 
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            backgroundColor: '#f8fafc',
                            color: '#334155',
                            border: '1px solid #e2e8f0',
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                            fontWeight: 700
                          }}
                          title={`Repertorios: ${t.repertorios.map(r => r.numero_repertorio).join(', ')} (${t.repertorios.length} en total)`}
                        >
                          <BookOpen size={12} color="#64748b" />
                          {formatRepertoriosRange(t.repertorios)}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8', fontStyle: 'italic', padding: '2px 6px', borderRadius: 4, background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                          Sin asignar
                        </span>
                      )}
                    </td>

                    {/* Solicitante / Tipo */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', maxWidth: '240px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.nombre_solicitante}>
                        {t.nombre_solicitante}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.tipo_inscripcion?.nombre}>
                          {t.tipo_inscripcion?.nombre || 'Inscripción'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setRectificarModal({ isOpen: true, tramite: t })}
                          title="Rectificar Tipo de Trámite ante llamado o error"
                          style={{
                            background: '#fff7ed',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            borderRadius: '4px',
                            padding: '1px 5px',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                            flexShrink: 0
                          }}
                        >
                          <Edit3 size={10} /> Corregir
                        </button>
                      </div>
                    </td>

                    {/* Área */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0'
                      }}>
                        {t.area?.nombre || 'General'}
                      </span>
                    </td>

                    {/* Estado con punto indicador */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const cfg = ESTADO_CONFIG[t.estado?.nombre] || { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0', dot: '#94a3b8' };
                        return (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '3px 9px',
                            borderRadius: '20px',
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            backgroundColor: cfg.bg,
                            color: cfg.text,
                            border: `1px solid ${cfg.border}`
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
                            {t.estado?.nombre || 'Sin estado'}
                          </span>
                        );
                      })()}
                    </td>

                    {/* Gestión (Funcionario) */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                      {t.estado?.nombre === 'Completado' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#059669', fontWeight: 600, fontSize: '0.8rem' }}>
                          <CheckCircle size={13} /> Finalizado
                        </span>
                      ) : canAssign ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <select 
                            style={{
                              fontSize: '0.8rem',
                              padding: '5px 8px',
                              borderRadius: '6px',
                              border: t.asignado_a ? '1px solid #93c5fd' : '1px solid #cbd5e1',
                              backgroundColor: t.asignado_a ? '#eff6ff' : '#ffffff',
                              color: t.asignado_a ? '#1d4ed8' : '#64748b',
                              fontWeight: t.asignado_a ? 600 : 400,
                              cursor: 'pointer',
                              minWidth: '160px',
                              outline: 'none',
                              transition: 'all 0.15s ease'
                            }}
                            value={t.asignado_a || ''}
                            onChange={(e) => handleAssign(t.id, e.target.value)}
                            disabled={assigning[t.id]}
                            title="Asignar o reasignar funcionario responsable del trámite"
                          >
                            <option value="">👤 Sin asignar</option>
                            {staffList.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                          {t.asignado_a === user?.id && (
                            <span title="Asignado a ti" style={{
                              background: '#3b82f6',
                              color: '#ffffff',
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              padding: '2px 5px',
                              borderRadius: '4px',
                              letterSpacing: '0.03em'
                            }}>
                              TÚ
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {t.asignado_a ? (
                            <div style={{
                              fontSize: '0.8rem',
                              color: t.asignado_a === user?.id ? '#1d4ed8' : '#334155',
                              fontWeight: t.asignado_a === user?.id ? 700 : 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              background: t.asignado_a === user?.id ? '#eff6ff' : '#f8fafc',
                              border: t.asignado_a === user?.id ? '1px solid #bfdbfe' : '1px solid #e2e8f0'
                            }}>
                              <User size={13} />
                              <span>{t.asignado_a === user?.id ? 'Asignado a ti' : (t.assigned_to?.name || 'Asignado')}</span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                padding: '4px 9px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              onClick={() => handleAssign(t.id, user.id)}
                              disabled={assigning[t.id]}
                              title="Autoasignarte este trámite para procesarlo"
                            >
                              ➕ Tomar trámite
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Caja */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {t.cotizacion?.caja ? (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          color: '#166534',
                          fontSize: '0.78rem',
                          fontWeight: 600
                        }}>
                          <DollarSign size={12} color="#16a34a" />
                          <span>{t.cotizacion.caja.name}</span>
                          {t.cotizacion?.usuario_caja_id === user?.id && (
                            <span style={{ background: '#16a34a', color: '#fff', fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, fontWeight: 800 }}>TÚ</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>

                    {/* Acciones unificadas en una sola fila */}
                    <td style={{ padding: '14px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexWrap: 'nowrap' }}>
                        <button 
                          type="button"
                          className="btn btn-sm"
                          style={{
                            padding: '5px 9px',
                            fontSize: '0.76rem',
                            fontWeight: 600,
                            background: '#4338ca',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 2px rgba(67, 56, 202, 0.2)'
                          }}
                          onClick={() => setRepertorioModal({ isOpen: true, tramite: t })}
                          title="Gestionar Repertorios"
                        >
                          <BookOpen size={13} /> Reptos.
                        </button>

                        {(t.estado?.nombre === 'Ingresado' || t.estado?.nombre === 'Procesando') && (
                          <button 
                            type="button"
                            className="btn btn-sm" 
                            style={{
                              padding: '5px 9px',
                              fontSize: '0.76rem',
                              fontWeight: 600,
                              background: '#059669',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              opacity: ((!t.repertorios || t.repertorios.length === 0) && !t.exento_repertorio) ? 0.45 : 1,
                              cursor: ((!t.repertorios || t.repertorios.length === 0) && !t.exento_repertorio) ? 'not-allowed' : 'pointer',
                              boxShadow: '0 1px 2px rgba(5, 150, 105, 0.2)'
                            }} 
                            title="Cotizar Trámite"
                            disabled={(!t.repertorios || t.repertorios.length === 0) && !t.exento_repertorio}
                            onClick={() => setCotizacionModal({ isOpen: true, tramite: t })}
                          >
                            <DollarSign size={13} /> Cotizar
                          </button>
                        )}

                        {(t.estado?.nombre === 'Pagado' || t.estado?.nombre === 'Devuelto') && (
                          <button 
                            type="button"
                            className="btn btn-sm" 
                            style={{
                              padding: '5px 9px',
                              fontSize: '0.76rem',
                              fontWeight: 600,
                              background: '#0d9488',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 1px 2px rgba(13, 148, 136, 0.2)'
                            }} 
                            title="Finalizar y Entregar"
                            onClick={() => setFinalizarModal({ isOpen: true, tramite: t })}
                          >
                            <CheckCircle size={13} /> Finalizar
                          </button>
                        )}

                        <button 
                          type="button"
                          className="btn btn-sm"
                          style={{
                            padding: '5px 10px',
                            fontSize: '0.76rem',
                            fontWeight: 600,
                            background: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                          }}
                          onClick={() => navigate(`/tramite/historial/${t.id}`, { state: { from: 'console' } })}
                          title="Ver detalle y gestionar"
                        >
                          Gestionar <ChevronRight size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {repertorioModal.isOpen && (
        <GestionarRepertoriosModal
            isOpen={repertorioModal.isOpen}
            onClose={() => setRepertorioModal({ isOpen: false, tramite: null })}
            tramite={repertorioModal.tramite}
            onUpdated={cargarTramites}
        />
      )}

      {cotizacionModal.isOpen && (
        <CotizacionTramiteModal 
          tramite={cotizacionModal.tramite}
          onClose={() => setCotizacionModal({ isOpen: false, tramite: null })}
          onUpdate={cargarTramites}
        />
      )}

      {finalizarModal.isOpen && (
        <FinalizarTramiteModal 
          tramite={finalizarModal.tramite}
          onClose={() => setFinalizarModal({ isOpen: false, tramite: null })}
          onUpdate={cargarTramites}
        />
      )}

      {rectificarModal.isOpen && (
        <RectificarTramiteModal
          tramite={rectificarModal.tramite}
          onClose={() => setRectificarModal({ isOpen: false, tramite: null })}
          onSuccess={cargarTramites}
        />
      )}
    </div>
  );
}
